
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { toErrorMessage } from '@/utils/errorHandling';
import TextField from '@/components/shared/TextField.vue';
import Toast from '@/utils/toast';

const namespace: string = 'rallyIntelligence';

@Component<PromptsConfiguration>({
	components: {
		TextField
	}
})
export default class PromptsConfiguration extends Vue {
	@Getter('getPrompts', { namespace }) private prompts!: RallyIntelligencePromptResponse[];
	@Getter('getConfiguration', { namespace }) private configuration!: RallyIntelligenceConfigurationResponse | null;
	@Getter('getMonthlyTokensRemaining', { namespace }) private tokensRemaining!: number;

	@Action('fetchPrompts', { namespace }) private fetchPrompts!: () => Promise<void>;
	@Action('fetchConfiguration', { namespace }) private fetchConfiguration!: () => Promise<void>;
	@Action('fetchUsage', { namespace }) private fetchUsage!: () => Promise<void>;
	@Action('updateConfiguration', { namespace }) private updateConfiguration!: (payload: UpdateRallyIntelligenceConfigRequest) => Promise<void>;
	@Action('setProviderCredential', { namespace }) private setProviderCredential!: (payload: SetProviderCredentialRequest) => Promise<void>;
	@Action('testConnection', { namespace }) private testConnection!: () => Promise<RallyIntelligenceTestConnectionResponse | undefined>;

	@Watch('configuration', { immediate: true })
	private onConfigurationChange(config: RallyIntelligenceConfigurationResponse | null): void {
		if (!config) {
			return;
		}
		this.isEnabled = config.is_enabled;
		this.provider = config.provider;
		this.activePromptId = config.active_prompt_id;
		this.monthlyTokenLimit = config.monthly_token_limit;
		this.apiKey = this.getStoredApiKey(config.provider);
	}

	@Watch('provider')
	private onProviderChange(provider: RallyIntelligenceProvider): void {
		this.apiKey = this.getStoredApiKey(provider);
	}

	private isEnabled: boolean = false;
	private saving: boolean = false;
	private testingConnection: boolean = false;
	private showApiKey: boolean = false;
	private provider: RallyIntelligenceProvider = 'openai';
	private activePromptId: number | null = null;
	private monthlyTokenLimit: number = 0;
	private apiKey: string = '';

	private readonly providerOptions: { text: string; value: RallyIntelligenceProvider }[] = [
		{ text: 'OpenAI', value: 'openai' },
		{ text: 'Gemini', value: 'gemini' },
		{ text: 'Ollama', value: 'ollama' }
	];

	private get activePromptOptions(): { text: string; value: number }[] {
		return this.prompts.map(p => ({ text: p.name, value: p.id }));
	}

	private async mounted(): Promise<void> {
		try {
			await this.fetchConfiguration();
			if (!this.prompts.length) {
				await this.fetchPrompts();
			}
		}
		catch (error) {
			Toast.error(this.$t('rally-intelligence.configuration.error_fetching', { errorMessage: toErrorMessage(error) }));
		}
		// Best-effort refresh of the tokens-remaining counter — a failed refresh
		// just leaves the stale count, so there's no toast on error.
		this.fetchUsage().catch(() => {});
	}

	private getStoredApiKey(provider: RallyIntelligenceProvider): string {
		return this.configuration?.credentials?.find(c => c.provider === provider)?.api_key ?? '';
	}

	/**
	 * Persist any changes the user made on the configuration tab: config fields
	 * (provider, active prompt, token limit, etc.) and the API key are saved
	 * through separate endpoints, so they're dispatched in sequence here.
	 *
	 * @return {Promise<void>}
	 */
	private async handleSave(): Promise<void> {
		// Guard against double-submits while a save is already in flight
		if (this.saving) {
			return;
		}
		this.saving = true;

		const provider = this.provider;
		const newApiKey = this.apiKey.trim();

		try {
			// Only send the config update when at least one field actually changed
			const payload = this.buildUpdatePayload();
			if (Object.keys(payload).length) {
				await this.updateConfiguration(payload);
			}
			// API key is stored on a separate endpoint, and only when it's actually new
			if (newApiKey && newApiKey !== this.getStoredApiKey(provider)) {
				await this.setProviderCredential({ provider, api_key: newApiKey });
			}
			// Refresh usage only when the limit changed — the remaining-tokens count is derived from it
			if (payload.monthly_token_limit !== undefined) {
				await this.fetchUsage();
			}
			Toast.success(this.$t('rally-intelligence.configuration.toast_saved'));
		}
		catch (error) {
			Toast.error(this.$t('rally-intelligence.configuration.error_saving', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.saving = false;
		}
	}

	/**
	 * Build a diff between the current form state and the saved configuration:
	 * only fields the user actually changed are attached to the payload, so the
	 * caller can skip the request entirely when nothing changed.
	 *
	 * @return {UpdateRallyIntelligenceConfigRequest}
	 */
	private buildUpdatePayload(): UpdateRallyIntelligenceConfigRequest {
		const config = this.configuration;
		const payload: UpdateRallyIntelligenceConfigRequest = {};
		// Nothing loaded yet — return an empty payload and let the caller skip the save
		if (!config) {
			return payload;
		}
		if (this.isEnabled !== config.is_enabled) {
			payload.is_enabled = this.isEnabled;
		}
		if (this.provider !== config.provider) {
			payload.provider = this.provider;
		}
		// activePromptId 0/null means "no prompt selected" — don't send it as a change
		if (this.activePromptId && this.activePromptId !== config.active_prompt_id) {
			payload.active_prompt_id = this.activePromptId;
		}
		// Guard against NaN (empty input) and negative values before sending the limit
		if (Number.isFinite(this.monthlyTokenLimit) && this.monthlyTokenLimit >= 0 && this.monthlyTokenLimit !== config.monthly_token_limit) {
			payload.monthly_token_limit = this.monthlyTokenLimit;
		}
		return payload;
	}

	private async testConnectionHandler(): Promise<void> {
		if (this.testingConnection) {
			return;
		}
		this.testingConnection = true;
		try {
			const response = await this.testConnection();
			if (response?.connected) {
				Toast.success(this.$t('rally-intelligence.configuration.toast_test_connection'));
			}
			else {
				Toast.error(this.buildTestConnectionError(response));
			}
		}
		catch {
			Toast.error(this.$t('rally-intelligence.configuration.toast_test_connection_failed'));
		}
		finally {
			this.testingConnection = false;
		}
	}

	private buildTestConnectionError(response: RallyIntelligenceTestConnectionResponse | undefined): string {
		if (response && !response.provider.ok && response.provider.reason) {
			const provider = this.providerOptions.find(o => o.value === response.provider.name)?.text ?? response.provider.name;
			return this.$t('rally-intelligence.configuration.toast_test_connection_failed_provider', {
				provider,
				reason: response.provider.reason
			});
		}
		if (response && !response.menu.available && response.menu.reason) {
			return this.$t('rally-intelligence.configuration.toast_test_connection_failed_menu', {
				reason: response.menu.reason
			});
		}
		return this.$t('rally-intelligence.configuration.toast_test_connection_failed');
	}
}
